import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import type { Mitglied } from '@beholder/shared';
import type { MindmapSpalte } from '@beholder/shared/module/mindmap';
import { api } from '../../core/api';
import { useLiveVerbunden } from '../../core/live';
import type { ModulSeiteProps } from '../../core/modul';
import { Symbol } from '../../core/symbole';
import { Dialog, Knopf, Laden, Leer, Meldung, useDaten } from '../../core/ui';
import { nachkommen } from './baum';
import { alsBlatt, csvDatei, dateiName, herunterladen, mindmapDrucken, mindmapPng, mindmapSvg, tabelleDrucken, xlsxDatei } from './export';
import { ImportDialog } from './ImportDialog';
import { KnotenPanel } from './KnotenPanel';
import { MindmapAnsicht } from './MindmapAnsicht';
import { SpaltenDialog } from './SpaltenDialog';
import { kuerzel, personFarbe } from './symbole';
import { TabellenAnsicht, type Befehl } from './TabellenAnsicht';
import { useEingeklappt, useKarte } from './useKarte';
import { wertText } from './werte';

export function Editor({ projekt, ich, darfSchreiben }: ModulSeiteProps) {
  const { karteId = '', '*': rest = '' } = useParams();
  const ansicht = rest.startsWith('tabelle') ? 'tabelle' : 'mindmap';
  const z = useKarte(projekt.id, karteId, ich, darfSchreiben);
  const nav = useNavigate();
  const { zu, setzen, umschalten } = useEingeklappt(karteId);
  const [auswahl, setAuswahl] = useState<string | null>(null);
  const [panel, setPanel] = useState(() => window.innerWidth > 900);
  const [befehl, setBefehl] = useState<Befehl | null>(null);
  const [spaltenDialog, setSpaltenDialog] = useState<{ offen: boolean; spalte: MindmapSpalte | null }>({ offen: false, spalte: null });
  const [importOffen, setImportOffen] = useState(false);
  const [menue, setMenue] = useState(false);
  const [hilfe, setHilfe] = useState(false);
  const [bearbeitet, setBearbeitet] = useState(false);
  const [titelEdit, setTitelEdit] = useState<string | null>(null);
  const verbunden = useLiveVerbunden();
  const mitglieder = useDaten(() => api.get<Mitglied[]>(`/api/projekte/${projekt.id}/mitglieder`), [projekt.id]);
  const personen = useMemo(() => new Map((mitglieder.daten ?? []).map((m) => [m.benutzerId, m.anzeigename])), [mitglieder.daten]);
  const menueRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  // Der Editor füllt genau den Rest des Fensters (die Seite selbst scrollt nicht mit)
  useEffect(() => {
    const anpassen = () => {
      const el = editorRef.current;
      if (!el) return;
      const oben = el.getBoundingClientRect().top + window.scrollY;
      el.style.height = `${Math.max(420, window.innerHeight - oben)}px`;
    };
    anpassen();
    const t = setTimeout(anpassen, 50);
    window.addEventListener('resize', anpassen);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', anpassen);
    };
  });

  const waehlen = (id: string | null, oeffnen?: boolean) => {
    setAuswahl(id);
    // Auf schmalen Bildschirmen öffnet das Antippen gleich die Details (als Blatt von unten)
    if (oeffnen || (id && window.innerWidth <= 900)) setPanel(true);
  };

  // Ausgewählten Punkt verloren (z. B. von jemand anderem gelöscht)
  useEffect(() => {
    if (auswahl && z.karte && !z.knoten[auswahl]) setAuswahl(null);
  }, [auswahl, z.knoten, z.karte]);

  const { fokus } = z;
  useEffect(() => fokus(auswahl, bearbeitet), [auswahl, bearbeitet, fokus]);
  useEffect(() => () => fokus(null, false), [fokus]);

  useEffect(() => {
    if (!menue) return;
    const h = (e: MouseEvent) => !menueRef.current?.contains(e.target as Node) && setMenue(false);
    window.addEventListener('mousedown', h);
    return () => window.removeEventListener('mousedown', h);
  }, [menue]);

  const loeschenFragen = (id: string) => {
    const k = z.knoten[id];
    if (!k?.elternId) return;
    const unter = nachkommen(z.baum, id);
    const dateien = [id, ...unter].reduce((s, x) => s + (z.knoten[x]?.anlagen ?? 0), 0);
    if (unter.length || dateien) {
      const teile = [unter.length ? `${unter.length} Unterpunkt${unter.length === 1 ? '' : 'en'}` : '', dateien ? `${dateien} Anlage${dateien === 1 ? '' : 'n'}` : '']
        .filter(Boolean)
        .join(' und ');
      if (!confirm(`„${k.titel}“ samt ${teile} in den Papierkorb legen? Dort lässt sich alles 180 Tage lang zurückholen.`)) return;
    }
    const geschw = z.baum.kinder.get(k.elternId) ?? [];
    const i = geschw.findIndex((x) => x.id === id);
    setAuswahl(geschw[i - 1]?.id ?? geschw[i + 1]?.id ?? k.elternId);
    void z.loeschen(id);
  };

  // Strg+Z / Strg+Y (bzw. Strg+Umschalt+Z) – nicht in Eingabefeldern, dort gilt das Rückgängig des Browsers
  const { rueckgaengig, wiederholen } = z;
  useEffect(() => {
    if (!darfSchreiben) return;
    const h = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest?.('input, textarea, select, [contenteditable="true"], dialog')) return;
      const taste = e.key.toLowerCase();
      if (taste === 'z' && !e.shiftKey) {
        e.preventDefault();
        void rueckgaengig();
      } else if (taste === 'y' || (taste === 'z' && e.shiftKey)) {
        e.preventDefault();
        void wiederholen();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [darfSchreiben, rueckgaengig, wiederholen]);

  async function exportieren(art: 'png' | 'svg' | 'csv' | 'xlsx' | 'druck-karte' | 'druck-tabelle') {
    setMenue(false);
    if (!z.karte) return;
    try {
      const titel = z.karte.titel;
      if (art === 'druck-karte') return mindmapDrucken(z.baum, zu, titel);
      if (art === 'druck-tabelle') return tabelleDrucken(alsBlatt(titel, z.baum, z.spalten, personen));
      if (art === 'svg' || art === 'png') {
        const s = mindmapSvg(z.baum, zu);
        if (art === 'svg') herunterladen(dateiName(titel, 'svg'), new Blob([s.svg], { type: 'image/svg+xml' }));
        else herunterladen(dateiName(titel, 'png'), await mindmapPng(s.svg, s.breite, s.hoehe));
      } else {
        const b = alsBlatt(titel, z.baum, z.spalten, personen);
        herunterladen(dateiName(titel, art), art === 'csv' ? csvDatei(b) : await xlsxDatei(b));
      }
    } catch (e) {
      z.setFehler(e instanceof Error ? e.message : 'Export fehlgeschlagen');
    }
  }

  if (z.geloescht) {
    return (
      <div className="seite">
        <Leer titel="Diese Mindmap gibt es nicht (mehr).">
          <Link to={`/p/${projekt.id}/mindmap`}>Zur Übersicht der Mindmaps</Link>
        </Leer>
      </div>
    );
  }
  if (!z.karte) return z.fehler ? <div className="seite"><Meldung>{z.fehler}</Meldung></div> : <Laden />;

  const k = auswahl ? z.knoten[auswahl] : undefined;
  const andere = z.anwesend.filter((p, i, alle) => alle.findIndex((x) => x.benutzerId === p.benutzerId) === i);
  const basis = `/p/${projekt.id}/mindmap/${karteId}`;
  const zielKnoten = k ?? z.baum.wurzel;

  return (
    <div className="mm-editor" ref={editorRef}>
      <div className="mm-leiste">
        <Link to={`/p/${projekt.id}/mindmap`} className="knopf knopf-leise knopf-klein" title="Alle Mindmaps" aria-label="Alle Mindmaps">
          {Symbol.pfeilLinks}
        </Link>
        {titelEdit !== null ? (
          <input
            className="eingabe mm-karten-titel-eingabe"
            autoFocus
            value={titelEdit}
            maxLength={500}
            onChange={(e) => setTitelEdit(e.target.value)}
            onBlur={() => {
              const t = titelEdit.trim();
              if (t && t !== z.karte!.titel) void z.aendern(z.karte!.wurzelId, { titel: t }, { titel: z.karte!.titel });
              setTitelEdit(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') setTitelEdit(null);
            }}
          />
        ) : (
          <h2 className="mm-karten-titel" title={darfSchreiben ? 'Klicken zum Umbenennen' : undefined} onClick={() => darfSchreiben && setTitelEdit(z.karte!.titel)}>
            {z.karte.titel}
          </h2>
        )}
        <nav className="mm-umschalter" aria-label="Ansicht">
          <NavLink end to={basis}>
            Mindmap
          </NavLink>
          <NavLink to={`${basis}/tabelle`}>Tabelle</NavLink>
        </nav>
        <span className="abstand" />
        {!verbunden && <span className="marke marke-warn" title="Änderungen anderer erscheinen nach dem Neuverbinden">getrennt</span>}
        <div className="mm-personen" aria-label="Gerade dabei">
          {andere.map((p) => (
            <span key={p.benutzerId} className="mm-person" style={{ background: personFarbe(p.benutzerId) }} title={`${p.name}${p.benutzerId === ich.id ? ' (du)' : ''}`}>
              {kuerzel(p.name)}
            </span>
          ))}
        </div>
        {darfSchreiben && (
          <>
            <Knopf
              klein
              art="leise"
              disabled={!z.stapel.rueck}
              onClick={() => void z.rueckgaengig()}
              title={z.stapel.rueck ? `Rückgängig: ${z.stapel.rueck} (Strg+Z)` : 'Nichts rückgängig zu machen'}
              aria-label="Rückgängig"
            >
              ↶
            </Knopf>
            <Knopf
              klein
              art="leise"
              disabled={!z.stapel.vor}
              onClick={() => void z.wiederholen()}
              title={z.stapel.vor ? `Wiederholen: ${z.stapel.vor} (Strg+Y)` : 'Nichts zu wiederholen'}
              aria-label="Wiederholen"
            >
              ↷
            </Knopf>
          </>
        )}
        <Knopf klein art="leise" onClick={() => setHilfe(true)} title="Tastenkürzel" aria-label="Hilfe">
          ?
        </Knopf>
        <Knopf klein art={panel ? 'normal' : 'leise'} onClick={() => setPanel(!panel)} title="Seitenleiste ein/aus" className="mm-nur-breit">
          Details
        </Knopf>
        <div className="mm-menue-rahmen" ref={menueRef}>
          <Knopf klein onClick={() => setMenue(!menue)} aria-expanded={menue}>
            Export ▾
          </Knopf>
          {menue && (
            <div className="mm-menue" role="menu">
              <button type="button" role="menuitem" onClick={() => void exportieren('png')}>
                Bild (PNG)
              </button>
              <button type="button" role="menuitem" onClick={() => void exportieren('svg')}>
                Vektorbild (SVG)
              </button>
              <button type="button" role="menuitem" onClick={() => void exportieren('xlsx')}>
                Tabelle für Excel (.xlsx)
              </button>
              <button type="button" role="menuitem" onClick={() => void exportieren('csv')}>
                Tabelle als CSV
              </button>
              <hr />
              <button type="button" role="menuitem" onClick={() => void exportieren('druck-karte')}>
                Mindmap drucken …
              </button>
              <button type="button" role="menuitem" onClick={() => void exportieren('druck-tabelle')}>
                Tabelle drucken …
              </button>
              {darfSchreiben && (
                <>
                  <hr />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenue(false);
                      setImportOffen(true);
                    }}
                  >
                    Datei einlesen unter „{zielKnoten?.titel}“ …
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <Meldung>{z.fehler}</Meldung>

      <div className="mm-arbeit">
        <div className="mm-ansicht">
          {ansicht === 'mindmap' ? (
            <MindmapAnsicht
              z={z}
              zu={zu}
              umschalten={umschalten}
              auswahl={auswahl}
              waehlen={waehlen}
              darfSchreiben={darfSchreiben}
              ich={ich}
              loeschenFragen={loeschenFragen}
              bearbeitetMelden={setBearbeitet}
              befehl={befehl}
            />
          ) : (
            <TabellenAnsicht
              z={z}
              zu={zu}
              umschalten={umschalten}
              setzeZu={setzen}
              auswahl={auswahl}
              waehlen={waehlen}
              darfSchreiben={darfSchreiben}
              personen={personen}
              ich={ich}
              loeschenFragen={loeschenFragen}
              befehl={befehl}
              spalteBearbeiten={(spalte) => setSpaltenDialog({ offen: true, spalte })}
            />
          )}
        </div>
        {k && panel && (
          <KnotenPanel
            key={k.id}
            z={z}
            k={k}
            projekt={projekt}
            darfSchreiben={darfSchreiben}
            personen={personen}
            ich={ich}
            schliessen={() => (window.innerWidth > 900 ? setPanel(false) : setAuswahl(null))}
            waehlen={(id) => setAuswahl(id)}
            loeschenFragen={loeschenFragen}
            unterpunkt={(id) => setBefehl({ art: 'kind', id, nr: (befehl?.nr ?? 0) + 1 })}
          />
        )}
      </div>

      <SpaltenDialog offen={spaltenDialog.offen} spalte={spaltenDialog.spalte} z={z} schliessen={() => setSpaltenDialog({ offen: false, spalte: null })} />
      <ImportDialog
        offen={importOffen}
        schliessen={() => setImportOffen(false)}
        projektId={projekt.id}
        ziel={zielKnoten ? { karteId, elternId: zielKnoten.id, elternTitel: zielKnoten.titel, vorhandeneSpalten: z.spalten.map((s) => s.name) } : undefined}
        personen={[...personen.values()]}
        fertig={(id) => {
          setImportOffen(false);
          // Bild als neue Mindmap übernommen → dorthin wechseln
          if (id !== karteId) nav(`/p/${projekt.id}/mindmap/${id}`);
          else void z.neuLaden();
        }}
      />
      <KonfliktDialog z={z} personen={personen} />
      <Dialog titel="Tastenkürzel" offen={hilfe} schliessen={() => setHilfe(false)}>
        <dl className="mm-hilfe">
          <dt>Klick</dt>
          <dd>Punkt auswählen und Details zeigen</dd>
          <dt>Doppelklick / F2 / Lostippen</dt>
          <dd>Titel ändern</dd>
          <dt>Enter</dt>
          <dd>neuer Punkt daneben (Geschwister)</dd>
          <dt>Tab / Einfg</dt>
          <dd>neuer Unterpunkt (Kind)</dd>
          <dt>Entf</dt>
          <dd>Punkt löschen</dd>
          <dt>Leertaste</dt>
          <dd>Ast ein- oder ausklappen</dd>
          <dt>Pfeiltasten</dt>
          <dd>zwischen Punkten wandern</dd>
          <dt>Ziehen</dt>
          <dd>Punkt auf einen anderen ziehen = darunter hängen; an den oberen/unteren Rand = davor/danach</dd>
          <dt>Mausrad / zwei Finger</dt>
          <dd>zoomen; Hintergrund ziehen = Ansicht verschieben; 0 = alles zeigen</dd>
          <dt>Strg+Enter</dt>
          <dd>Details öffnen</dd>
          <dt>Strg+Z / Strg+Y</dt>
          <dd>eigene Schritte rückgängig machen / wiederholen (Gelöschtes liegt außerdem 180 Tage im Papierkorb des Projekts)</dd>
        </dl>
      </Dialog>
      {!darfSchreiben && <p className="mm-nur-lesen leise klein">Nur ansehen</p>}
    </div>
  );
}

function KonfliktDialog({ z, personen }: { z: ReturnType<typeof useKarte>; personen: Map<string, string> }) {
  const k = z.konflikt;
  if (!k) return null;
  const zeige = (feld: string, quelle: 'server' | 'meine') => {
    if (feld.startsWith('werte.')) {
      const sid = feld.slice(6);
      const sp = z.spalten.find((s) => s.id === sid);
      const w = quelle === 'server' ? k.server.werte[sid] : k.meine.werte?.[sid];
      return { name: sp?.name ?? 'Wert', text: sp ? wertText(sp, w ?? null, personen) || '(leer)' : String(w ?? '') };
    }
    const namen: Record<string, string> = { titel: 'Titel', beschreibung: 'Beschreibung', farbe: 'Farbe', symbol: 'Symbol' };
    const w = (quelle === 'server' ? k.server : k.meine)[feld as 'titel'];
    return { name: namen[feld] ?? feld, text: (w as string | null | undefined) || '(leer)' };
  };
  return (
    <Dialog
      titel="Gleichzeitig geändert"
      offen
      schliessen={() => z.setKonflikt(null)}
      fuss={
        <>
          <Knopf
            art="haupt"
            onClick={() => {
              void z.aendern(k.knotenId, k.meine, undefined, true);
              z.setKonflikt(null);
            }}
          >
            Meine Eingabe übernehmen
          </Knopf>
          <Knopf onClick={() => z.setKonflikt(null)}>Die andere Fassung behalten</Knopf>
        </>
      }
    >
      <div className="stapel">
        <p>
          <strong>{k.server.geaendertVonName ?? 'Jemand'}</strong> hat „{k.server.titel}“ geändert, während du daran gearbeitet hast. Nichts wurde
          überschrieben – entscheide, was gelten soll.
        </p>
        {k.felder.map((f) => {
          const s = zeige(f, 'server');
          const m = zeige(f, 'meine');
          return (
            <div key={f} className="mm-konflikt">
              <strong>{s.name}</strong>
              <div>
                <span className="leise klein">Jetzt gespeichert:</span> <span className="mm-text-block">{s.text}</span>
              </div>
              <div>
                <span className="leise klein">Deine Eingabe:</span> <span className="mm-text-block">{m.text}</span>
              </div>
            </div>
          );
        })}
      </div>
    </Dialog>
  );
}
